<div class="funding-view">
  <div class="funding-header">
    <div>
      <h2 class="section-heading">Claims</h2>
      <p class="funding-subtitle">Expense claims you have submitted against your award.</p>
    </div>
    <div class="card-actions">
      <button type="button" class="btn btn-primary" (click)="openForm()" [disabled]="categories().length === 0">
        <i class="fas fa-plus"></i> Submit a claim
      </button>
      <button type="button" class="btn btn-secondary btn-sm" (click)="load()">
        <i class="fas fa-sync"></i> Refresh
      </button>
    </div>
  </div>

  <div class="card">
    <div class="card-header">My expense claims</div>
    <div class="card-body">
      @if (categories().length === 0) {
        <p class="card-note">
          You can claim once you have an active award with a claimable benefit category.
        </p>
      }

      @if (error()) {
        <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ error() }}</div>
      }
      @if (message()) {
        <div class="alert alert-success"><i class="fas fa-check-circle"></i> {{ message() }}</div>
      }

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Claim</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr><td colspan="7" class="empty-cell"><i class="fas fa-spinner fa-spin"></i> Loading claims…</td></tr>
            }
            @if (!loading() && claims().length === 0) {
              <tr><td colspan="7" class="empty-cell">You have not submitted any claims yet.</td></tr>
            }
            @for (claim of claims(); track claim.id) {
              <tr>
                <td>{{ claim.referenceNumber }}</td>
                <td>
                  <strong>{{ claim.title }}</strong>
                  @if (claim.scholarshipTitle) { <small>{{ claim.scholarshipTitle }}</small> }
                </td>
                <td>{{ claim.benefitName }}</td>
                <td>{{ claim.amount | number: '1.2-2' }} {{ claim.currency }}</td>
                <td>{{ claim.submittedDate | date: 'mediumDate' }}</td>
                <td>
                  <span [class]="statusClass(claim.status)">{{ claim.status }}</span>
                  @if (claim.status === 'REJECTED' && claim.decisionReason) {
                    <small class="rejection-note">{{ claim.decisionReason }}</small>
                  }
                  @if (claim.status === 'PAID' && claim.paidDate) {
                    <small>Paid {{ claim.paidDate | date: 'mediumDate' }}</small>
                  }
                </td>
                <td>
                  <button type="button" class="btn btn-secondary btn-sm" (click)="openDetail(claim)">View</button>
                  @if (canResubmit(claim)) {
                    <button type="button" class="btn btn-primary btn-sm" (click)="openDetail(claim)">Respond</button>
                  }
                  @if (canWithdraw(claim)) {
                    <button type="button" class="btn btn-secondary btn-sm" (click)="cancel(claim)">Withdraw</button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

<!-- Submit a claim ------------------------------------------------------- -->
@if (showForm()) {
  <div class="modal-backdrop" (click)="closeForm()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Submit an expense claim</h2>
        <button class="modal-close" (click)="closeForm()"><i class="fas fa-times"></i></button>
      </div>

      <div class="modal-body">
        <form [formGroup]="claimForm" class="form-grid">
          <label>
            Benefit category
            <select formControlName="scholarshipAwardBenefitId">
              <option [ngValue]="null">Select a category…</option>
              @for (category of categories(); track category.scholarshipAwardBenefitId) {
                <option [ngValue]="category.scholarshipAwardBenefitId">
                  {{ category.name }} — {{ category.remainingAmount | number: '1.2-2' }} remaining
                </option>
              }
            </select>
          </label>

          <label>
            What is the claim for?
            <input type="text" formControlName="title" placeholder="e.g. Semester 1 textbooks" />
          </label>

          <label>
            Amount
            <input type="number" step="0.01" formControlName="amount" />
          </label>

          <label>
            Date incurred
            <input type="date" formControlName="dateIncurred" />
          </label>

          <label>
            Notes
            <textarea rows="3" formControlName="description"></textarea>
          </label>

          <div class="upload-block">
            <label class="upload-label">
              Supporting documents
              <input type="file" (change)="onFileSelected($event)" [disabled]="uploading()" />
            </label>
            @if (uploading()) {
              <small><i class="fas fa-spinner fa-spin"></i> Uploading…</small>
            }
            @if (pendingDocuments().length === 0 && !uploading()) {
              <small>At least one document (invoice, receipt or proof of payment) is required.</small>
            }
            <ul class="document-list">
              @for (document of pendingDocuments(); track document.objectKey) {
                <li>
                  <i class="fas fa-paperclip"></i> {{ document.fileName }}
                  <button type="button" class="inline-link" (click)="removeDocument(document.objectKey)">Remove</button>
                </li>
              }
            </ul>
          </div>
        </form>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeForm()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="submit()" [disabled]="busy() || uploading()">
          {{ busy() ? 'Submitting…' : 'Submit claim' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Claim detail / resubmission ------------------------------------------ -->
@if (selected(); as claim) {
  <div class="modal-backdrop" (click)="closeDetail()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Claim {{ claim.referenceNumber }}</h2>
        <button class="modal-close" (click)="closeDetail()"><i class="fas fa-times"></i></button>
      </div>

      <div class="modal-body">
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Status</span>
            <span class="detail-value"><span [class]="statusClass(claim.status)">{{ claim.status }}</span></span>
          </div>
          <div class="detail-item"><span class="detail-label">Category</span><span class="detail-value">{{ claim.benefitName }}</span></div>
          <div class="detail-item"><span class="detail-label">Amount</span>
            <span class="detail-value">{{ claim.amount | number: '1.2-2' }} {{ claim.currency }}</span>
          </div>
          <div class="detail-item"><span class="detail-label">Date incurred</span><span class="detail-value">{{ claim.dateIncurred | date: 'mediumDate' }}</span></div>
          <div class="detail-item"><span class="detail-label">Scholarship</span><span class="detail-value">{{ claim.scholarshipTitle || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Award</span><span class="detail-value">{{ claim.awardReferenceNumber || '—' }}</span></div>
          @if (claim.resubmissionCount > 0) {
            <div class="detail-item"><span class="detail-label">Resubmissions</span><span class="detail-value">{{ claim.resubmissionCount }}</span></div>
          }
          @if (claim.paidAmount) {
            <div class="detail-item"><span class="detail-label">Paid</span>
              <span class="detail-value">{{ claim.paidAmount | number: '1.2-2' }} {{ claim.currency }}
                @if (claim.paymentReference) { ({{ claim.paymentReference }}) }
              </span>
            </div>
          }
        </div>

        @if (claim.description) {
          <p class="detail-note">{{ claim.description }}</p>
        }

        @if (claim.status === 'REJECTED' && claim.decisionReason) {
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-circle"></i>
            <strong>Rejected:</strong> {{ claim.decisionReason }}
            <br />Upload a new document below and resubmit for review.
          </div>
        }

        <h3 class="section-heading">Documents</h3>
        <ul class="document-list">
          @if (claim.documents.length === 0) {
            <li>No documents attached.</li>
          }
          @for (document of claim.documents; track document.id) {
            <li>
              <i class="fas fa-file"></i> {{ document.fileName }}
              @if (document.isResubmission) { <span class="status status-booked">Resubmission</span> }
              <span class="status status-pending">{{ document.reviewStatus }}</span>
              <button type="button" class="inline-link" (click)="download(document.id)">Download</button>
            </li>
          }
        </ul>

        @if (showResubmit()) {
          <h3 class="section-heading">Attach a new document and resubmit</h3>
          <form [formGroup]="resubmitForm" class="form-grid">
            <label>
              Corrected amount (optional)
              <input type="number" step="0.01" formControlName="amount" />
            </label>
            <label>
              Notes
              <textarea rows="3" formControlName="description"></textarea>
            </label>
            <div class="upload-block">
              <label class="upload-label">
                New document
                <input type="file" (change)="onFileSelected($event)" [disabled]="uploading()" />
              </label>
              @if (uploading()) {
                <small><i class="fas fa-spinner fa-spin"></i> Uploading…</small>
              }
              <ul class="document-list">
                @for (document of pendingDocuments(); track document.objectKey) {
                  <li>
                    <i class="fas fa-paperclip"></i> {{ document.fileName }}
                    <button type="button" class="inline-link" (click)="removeDocument(document.objectKey)">Remove</button>
                  </li>
                }
              </ul>
            </div>
          </form>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDetail()">Close</button>
        @if (canResubmit(claim) && !showResubmit()) {
          <button type="button" class="btn btn-primary" (click)="startResubmit(claim)">
            Respond with a new document
          </button>
        }
        @if (showResubmit()) {
          <button type="button" class="btn btn-primary" (click)="resubmit()" [disabled]="busy() || uploading()">
            {{ busy() ? 'Resubmitting…' : 'Resubmit for review' }}
          </button>
        }
      </div>
    </div>
  </div>
}

